// @flow

import * as Sentry from '@sentry/react'

// Tag of the final event of a failed playback cycle. The app lets it through the SDK Dedupe:
// failures of different content look the same to it, the dedupe below tells them apart
export const PLAYBACK_FAILURE_TAG = 'playback_failure'

// Only aborted operations are excluded from final cycle reports. Recoverable network errors
// remain history-only until recovery is exhausted; one deduplicated failure is then reported.
export const EXCLUDED_SHAKA_CODES = [7001]

// Causes without an engine error of their own
const TIMEOUT_CAUSES = ['load_timeout', 'stall_timeout', 'cycle_timeout']

export const DEDUPE_WINDOW = 10 * 60 * 1000
export const DEDUPE_LIMIT = 50
export const HISTORY_LIMIT = 10

const MAX_TEXT = 300

export type PlaybackErrorDetails = {
  code?: string | number,
  category?: number,
  severity?: number,
  type?: string,
  details?: string,
  httpStatus?: number,
  fatal?: boolean,
  message?: string,
  stack?: string,
  mediaErrorCode?: number,
  mediaErrorMessage?: string,
  underlyingMessage?: string,
  // Message of the exception behind an hls.js error
  errorMessage?: string,
  url?: string,
  // hls.js failure that preceded the fallback to VHS
  p2p?: PlaybackErrorDetails,
}

// Query and fragment often carry tokens, user info carries credentials
export const sanitizeUrl = (url: mixed): ?string => {
  if (typeof url !== 'string' || !url) return undefined

  return url.split(/[?#]/)[0].replace(/\/\/[^/@]*@/, '//')
}

export const sanitizeText = (text: mixed, limit: number = MAX_TEXT): ?string => {
  if (typeof text !== 'string' || !text) return undefined

  return text.replace(/https?:\/\/[^\s"'<>]+/g, (url) => sanitizeUrl(url) || '').slice(0, limit)
}

const pickNumber = (value: mixed): ?number => (typeof value === 'number' ? value : undefined)

// Leaves out values that are not available
export const withoutEmpty = (object: Object): Object =>
  Object.keys(object).reduce(
    (result, key) => (object[key] === undefined ? result : { ...result, [key]: object[key] }),
    {},
  )

const pickString = (value: mixed): ?string => (typeof value === 'string' ? value : undefined)

// The allowed part of an hls.js ErrorData (1.6): no stream, response body or headers
// eslint-disable-next-line complexity
export const pickHlsErrorData = (data: ?Object): Object =>
  withoutEmpty({
    type: pickString(data?.type),
    details: pickString(data?.details),
    fatal: typeof data?.fatal === 'boolean' ? data.fatal : undefined,
    httpStatus: pickNumber(data?.httpStatus) ?? pickNumber(data?.response?.code),
    url: sanitizeUrl(data?.url || data?.response?.url || data?.context?.url || data?.frag?.url),
    errorMessage: sanitizeText(data?.errorMessage ?? data?.error?.message),
  })

// Compact, token-free description of an engine error, as the Shaka or Video.js container
// prepared it or as hls.js reported it. The stream object is left out: it may hold DRM headers
// eslint-disable-next-line complexity
export const describePlaybackError = (error: ?Object): PlaybackErrorDetails => {
  if (!error) return {}

  const raw = error.sentry || error
  const data = Array.isArray(raw.data) ? raw.data : []
  const hls = pickHlsErrorData(raw)
  const isNetwork = Number(raw.category) === 1 || [1001, 1002, 1003].includes(Number(raw.code))
  const code = Number(raw.code)
  const segmentUri = { 3014: 1, 3015: 2, 3018: 0, 3019: 0, 3020: 0, 3022: 0, 3023: 0 }[code]

  return withoutEmpty({
    code: error.code ?? raw.code,
    category: pickNumber(raw.category),
    severity: pickNumber(error.severity ?? raw.severity),
    type: hls.type,
    details: hls.details,
    fatal: hls.fatal,
    // Shaka keeps the URI and the HTTP status in data, the containers in status and source
    httpStatus:
      hls.httpStatus ??
      (isNetwork ? pickNumber(error.status?.code) ?? pickNumber(data[1]) : undefined),
    message: sanitizeText(error.message ?? raw.message),
    errorMessage: hls.errorMessage,
    stack: sanitizeText(error.stack ?? raw.stack, 2000),
    mediaErrorCode: Number(raw.code) === 3016 ? pickNumber(data[0]) : undefined,
    mediaErrorMessage: Number(raw.code) === 3016 ? sanitizeText(data[2]) : undefined,
    underlyingMessage: code === 3015 ? sanitizeText(data[0]?.message) : undefined,
    url:
      hls.url ||
      (isNetwork ? sanitizeUrl(data[0]) : sanitizeUrl(data[segmentUri])) ||
      sanitizeUrl(raw.source?.src),
    p2p: error.p2pError ? describePlaybackError(error.p2pError) : undefined,
  })
}

export type PlaybackFailure = {
  engine: string,
  contentId: string,
  contentTitle?: string,
  contentType?: string,
  stop?: string,
  stopError?: Object,
  cause: string,
  message: string,
  // The error that ended the cycle (none for a timeout) and the first one of the cycle: the original cause
  error?: PlaybackErrorDetails,
  origin?: PlaybackErrorDetails,
  // Distinct error codes of the current cycle
  cycleErrorCodes?: Array<string | number>,
  // Every attempt of the cycle: cause, ending error and the codes it saw; they decide on the report
  attempts?: Array<Object>,
  // Cycles before this one, +1 per Retry from the modal
  cycle?: number,
  // Last errors of this content, oldest first: diagnostics only
  history: Array<{ at: number, error: PlaybackErrorDetails }>,
  attempt: number,
  // State of the playback taken before the engine was stopped
  snapshot?: Object,
}

export const isExcludedError = (engine: string, details: ?PlaybackErrorDetails): boolean =>
  engine.indexOf('shaka') === 0 && EXCLUDED_SHAKA_CODES.includes(Number(details?.code))

// The attempts of a failure; a failure that lists none is one attempt
// eslint-disable-next-line complexity
const getAttempts = (failure: PlaybackFailure): Array<Object> =>
  failure.attempts && failure.attempts.length > 0
    ? failure.attempts
    : [{ cause: failure.cause, error: failure.error, codes: failure.cycleErrorCodes || [] }]

// An attempt that failed only because of excluded errors: ended by one, or a timeout whose attempt
// saw nothing but them. A timeout without engine errors, a stop that failed (cancel) or any
// other error is a failure worth reporting
// eslint-disable-next-line complexity
export const isExcludedAttempt = (engine: string, attempt: Object): boolean => {
  // The engine could not be stopped after it: a fault of the app's own, always reported
  if (attempt.stopFailed) return false

  if (attempt.cause === 'error') return isExcludedError(engine, attempt.error)

  if (!TIMEOUT_CAUSES.includes(attempt.cause)) return false

  const codes = attempt.codes || []

  return codes.length > 0 && codes.every((code) => isExcludedError(engine, { code }))
}

// Checked before the event gets its name, so an excluded error never comes back renamed.
// The whole cycle is judged attempt by attempt: it is excluded only when every attempt is.
// Only the current cycle counts: the history is not used, nor its length
export const isExcludedFailure = (failure: PlaybackFailure): boolean =>
  getAttempts(failure).every((attempt) => isExcludedAttempt(failure.engine, attempt))

// The attempt the report is named after: the last one that justifies sending it.
// Aborted operations do not hide the last substantive error or timeout.
export const getReportedAttempt = (failure: PlaybackFailure): ?Object => {
  const allowed = getAttempts(failure).filter(
    (attempt) => !isExcludedAttempt(failure.engine, attempt),
  )

  return allowed[allowed.length - 1]
}

// Fingerprint part: the cause of the failure and, for engine errors, its code
// eslint-disable-next-line complexity
export const getFailureCause = (cause: string, details: ?PlaybackErrorDetails): string => {
  if (cause !== 'error') return cause

  const code = details?.details || details?.code

  return code === undefined ? cause : `error_${code}`
}

// Last sending time by engine, cause and content; the oldest entry goes first over the limit
const sentFailures: Map<string, number> = new Map()

export const resetPlaybackFailureDedupe = (): void => sentFailures.clear()

// eslint-disable-next-line complexity
export const shouldSendFailure = (key: string, now: number = Date.now()): boolean => {
  const sentAt = sentFailures.get(key)

  if (sentAt !== undefined && now - sentAt < DEDUPE_WINDOW) return false

  sentFailures.delete(key)
  sentFailures.set(key, now)

  if (sentFailures.size > DEDUPE_LIMIT) {
    const oldest = sentFailures.keys().next().value

    if (oldest !== undefined) sentFailures.delete(oldest)
  }

  return true
}

// A flat overview for triage; pending requests are not mislabeled as failed HTTP requests.
// eslint-disable-next-line complexity
export const buildFailureSummary = (failure: PlaybackFailure, reported: Object): Object => {
  const diagnostics = reported.snapshot?.diagnostics || failure.snapshot?.diagnostics || {}
  const { error } = reported
  const lastNetworkError = [...(diagnostics.failures || [])]
    .reverse()
    .find((entry) => entry.error?.code !== 7001 && entry.error?.url)
  const pending = diagnostics.pendingRequests || []

  return withoutEmpty({
    channel: sanitizeText(failure.contentTitle),
    content_id: failure.contentId,
    engine: failure.engine,
    cause: getFailureCause(reported.cause, error),
    stage: failure.snapshot?.hadPlayback ? 'interrupted' : 'startup',
    source_url: sanitizeUrl(diagnostics.source),
    failed_request_url: sanitizeUrl(error?.url),
    http_status: error?.httpStatus,
    last_network_error_url: sanitizeUrl(lastNetworkError?.error?.url),
    last_network_http_status: lastNetworkError?.error?.httpStatus,
    pending_request_count: pending.length,
    pending_request_url: sanitizeUrl(pending[0]?.url),
    pending_request_type: pending[0]?.requestType,
    pending_request_elapsed_ms: pending[0]?.elapsedMs,
    attempts: failure.attempt,
    recovery_result: 'exhausted',
    stop_status: failure.stop,
  })
}

// One event per failed cycle. Order: excluded codes → own dedupe → capture;
// then the SDK InboundFilters, beforeSend (force_keep skips sampling) and the transport
// eslint-disable-next-line complexity
export const reportPlaybackFailure = (failure: PlaybackFailure): boolean => {
  const { engine, contentId, origin } = failure
  const reported = getReportedAttempt(failure)

  if (!reported) return false

  const { error } = reported
  const cause = getFailureCause(reported.cause, error)

  if (!shouldSendFailure(`${engine}|${cause}|${contentId}`)) return false

  // eslint-disable-next-line complexity
  Sentry.withScope((scope) => {
    scope.setContext('playback_summary', buildFailureSummary(failure, reported))
    scope.setTag(PLAYBACK_FAILURE_TAG, '1')
    scope.setTag('force_keep', '1')
    scope.setTag('player_engine', engine)
    scope.setTag('playback_cause', cause)
    scope.setTag('playback_diagnostics', '5')
    scope.setTag('playback_stage', failure.snapshot?.hadPlayback ? 'interrupted' : 'startup')
    scope.setTag('content_id', contentId)
    if (failure.contentType === 'TV') scope.setTag('channel_id', contentId)
    if (failure.contentTitle) scope.setTag('content_title', sanitizeText(failure.contentTitle))
    if (failure.contentType) scope.setTag('content_type', failure.contentType)
    if (reported.stopFailed || failure.stopError) scope.setTag('playback_stop_failed', '1')
    const diagnostics = failure.snapshot?.diagnostics
    if (diagnostics?.version) scope.setTag('player_version', diagnostics.version)
    if (diagnostics?.format) scope.setTag('stream_format', diagnostics.format)
    if (diagnostics?.cdnHost) scope.setTag('cdn_host', diagnostics.cdnHost)
    if (diagnostics)
      scope.setContext('player_diagnostics', {
        __sentry_override_normalization_depth__: 8,
        ...diagnostics,
      })
    // Keep attempt evidence shallow: server-side normalization also limits nesting,
    // independently of the SDK's normalization depth.
    const attempts = failure.attempts?.map((attempt, index) => {
      const context = `playback_attempt_${index + 1}`
      const attemptDiagnostics = attempt.snapshot?.diagnostics
      if (!attemptDiagnostics) return attempt

      scope.setContext(context, {
        __sentry_override_normalization_depth__: 8,
        ...attemptDiagnostics,
        attempt: attempt.attempt,
        cause: attempt.cause,
      })
      return {
        ...attempt,
        diagnosticsContext: context,
        snapshot: { ...attempt.snapshot, diagnostics: undefined },
      }
    })
    scope.setFingerprint([PLAYBACK_FAILURE_TAG, engine, cause])
    scope.setContext('playback', {
      // Preserve only our bounded diagnostic context; do not expand Redux state or other extras.
      __sentry_override_normalization_depth__: 8,
      contentId,
      contentTitle: sanitizeText(failure.contentTitle),
      contentType: failure.contentType,
      stop: failure.stop,
      stopError: failure.stopError,
      cause,
      attempt: failure.attempt,
      cycle: failure.cycle,
      attempts,
      message: sanitizeText(failure.message),
      error,
      origin,
      cycleErrorCodes: failure.cycleErrorCodes,
      history: failure.history,
      snapshot: failure.snapshot ? { ...failure.snapshot, diagnostics: undefined } : undefined,
    })

    const exception = new Error(`Playback failure: ${engine} ${cause}`)

    exception.name = 'PlaybackFailure'
    Sentry.captureException(exception)
  })

  return true
}
